// src/components/MaterialProducer.js
// frontend/src/components/MaterialProducer.js
import React, { useState, useEffect, useRef } from 'react'; // Adicionado useRef
import ReactDOM from 'react-dom';
import styles from './MaterialProducer.module.css';
import { useNotification } from '../contexts/NotificationContext';
import { Document, Page, pdfjs } from 'react-pdf';
import { useTranslation } from 'react-i18next';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://quiron.app/api';
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

function MaterialProducer({ isSidebarOpen }) {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  const [materialType, setMaterialType] = useState('summary'); // Changed initial state to 'summary'
  const [questionCount, setQuestionCount] = useState(5);
  const [questionType, setQuestionType] = useState('objective');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const { addNotification } = useNotification(); // Corrigido para addNotification

  const previewContainerRef = useRef(null);
  const [previewWidth, setPreviewWidth] = useState(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const updatePreviewWidth = () => {
      if (previewContainerRef.current) {
        setPreviewWidth(previewContainerRef.current.offsetWidth - 10);
      }
    };

    if (file && file.type === 'application/pdf') {
      updatePreviewWidth();
      window.addEventListener('resize', updatePreviewWidth);
    } else {
      setPreviewWidth(0);
    }

    return () => {
      window.removeEventListener('resize', updatePreviewWidth);
    };
  }, [file, isSidebarOpen]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setResult(null);
      setFileContent(null);

      const fileType = selectedFile.type;
      if (fileType === 'text/plain') {
        const reader = new FileReader();
        reader.onload = (event) => setFileContent(event.target.result);
        reader.readAsText(selectedFile);
      } else if (fileType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') {
        addNotification(t('pptxPreviewNotAvailable'), 'info');
      }
    } else {
      handleRemoveFile();
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setFileContent(null);
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!file) {
      addNotification(t('selectFile'), 'error');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadResponse = await fetch(`${API_BASE_URL}/upload`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      const uploadData = await uploadResponse.json();

      if (!uploadResponse.ok) {
        throw new Error(uploadData.error || t('uploadError'));
      }

      const payload = { filepath: uploadData.filepath, material_type: materialType };
      if (materialType === 'questionnaire') {
        payload.question_count = questionCount;
        payload.question_type = questionType;
      }
      const processResponse = await fetch(`${API_BASE_URL}/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include',
      });
      const processData = await processResponse.json();

      if (!processResponse.ok) {
        throw new Error(processData.error || t('processingError'));
      }

      setResult(processData);
      addNotification(t('materialGeneratedSuccess'), 'success');
    } catch (error) {
      console.error('Erro ao gerar material:', error);
      addNotification(`${t('error')}: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`${styles.materialProducer} ${!isSidebarOpen ? styles.adjustedForClosedSidebar : ''}`}>
      <div className={styles.inputContainer}>
        <div className={`${styles.fileContainer} ${file ? styles.fileSelected : ''}`}>
          <button
            className={styles.fileUploadButton}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          >
            {t('chooseFile')}
          </button>
          <input
            id="fileInput"
            ref={fileInputRef}
            type="file"
            onChange={handleFileChange}
            accept=".pdf,.pptx,.txt"
            style={{ display: 'none' }}
          />
          {file && (
            <>
              <span className={styles.fileName} title={file.name}>{file.name}</span>
              <button
                onClick={handleRemoveFile}
                className={styles.removeFileButton}
                title={t('removeFile')}
              >
                &times;
              </button>
            </>
          )}
        </div>
        {file && (
          <div className={styles.previewContainer} ref={previewContainerRef}>
            {file.type === 'application/pdf' && previewWidth > 0 && (
              <Document file={file} onLoadError={console.error}>
                <Page pageNumber={1} width={previewWidth} />
              </Document>
            )}
            {file.type === 'text/plain' && fileContent && (
              <pre className={styles.textPreview}>{fileContent}</pre>
            )}
            {file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' && (
              <p className={styles.previewPlaceholder}>{t('pptxPreviewNotAvailable')}</p>
            )}
          </div>
        )}
        <select
          onChange={(e) => setMaterialType(e.target.value)}
          value={materialType}
          className={styles.themeSelect}
        >
          <option value="detailed_text">{t('detailedText')}</option>
          <option value="summary">{t('summary')}</option>
          <option value="flashcards">{t('flashcards')}</option>
          <option value="mind_map">{t('mindMap')}</option>
          <option value="comparative_table">{t('comparativeTable')}</option>
          <option value="acronyms_mnemonics">{t('acronymsMnemonics')}</option>
          <option value="questionnaire">{t('questionnaire')}</option>
        </select>
        {materialType === 'questionnaire' && (
          <>
            <input
              type="number"
              min="1"
              value={questionCount}
              onChange={(e) => setQuestionCount(parseInt(e.target.value))}
              placeholder={t('numberOfQuestions')}
              className={styles.themeInput}
            />
            <select
              onChange={(e) => setQuestionType(e.target.value)}
              value={questionType}
              className={styles.themeSelect}
            >
              <option value="objective">{t('objective')}</option>
              <option value="subjective">{t('subjective')}</option>
            </select>
          </>
        )}
      </div>
      <div className={styles.buttonContainer}>
        <button
          onClick={handleSubmit}
          disabled={loading || !file}
          className={styles.themeButton}
        >
          {loading ? (
            <div className={styles.ecgAnimation}>
              <svg viewBox="0 0 100 25" preserveAspectRatio="none">
                <path
                  id="ecgPath"
                  className={styles.ecgLine}
                  d="M0,20 L10,20 L12,10 L14,30 L16,20 L18,20 L20,20 L22,10 L24,30 L26,20 L28,20 L30,20 L32,10 L34,30 L36,20 L38,20 L40,20 L42,10 L44,30 L46,20 L48,20 L50,20 L52,10 L54,30 L56,20 L58,20 L60,20 L62,10 L64,30 L66,20 L68,20 L70,20 L72,10 L74,30 L76,20 L78,20 L80,20 L82,10 L84,30 L86,20 L88,20 L90,20 L92,10 L94,30 L96,20 L98,20 L100,20"
                />
                <path
                  className={styles.ecgTrail}
                  d="M0,20 L10,20 L12,10 L14,30 L16,20 L18,20 L20,20 L22,10 L24,30 L26,20 L28,20 L30,20 L32,10 L34,30 L36,20 L38,20 L40,20 L42,10 L44,30 L46,20 L48,20 L50,20 L52,10 L54,30 L56,20 L58,20 L60,20 L62,10 L64,30 L66,20 L68,20 L70,20 L72,10 L74,30 L76,20 L78,20 L80,20 L82,10 L84,30 L86,20 L88,20 L90,20 L92,10 L94,30 L96,20 L98,20 L100,20"
                />
                <circle cx="0" cy="0" r="2" className={styles.ecgDot}>
                  <animateMotion dur="2s" repeatCount="indefinite" rotate="auto">
                    <mpath href="#ecgPath" />
                  </animateMotion>
                </circle>
              </svg>
            </div>
          ) : (
            t('generateMaterial')
          )}
        </button>
      </div>
      {result &&
        ReactDOM.createPortal(
          <div className={styles.resultContainer}>
            <h3 className="theme-subtitle">{t('result')}:</h3>
            <div className={styles.messageContainer}>
              <p className={styles.botMessage}>
                <strong>{t('quiron')}:</strong> {result.transcription || result.summary || JSON.stringify(result, null, 2)}
              </p>
            </div>
          </div>,
          document.getElementById('result-portal')
        )}
    </div>
  );
}

export default MaterialProducer;
